import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

const AppContext = createContext();
export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  const fetchCounts = useCallback(async () => {
    if (!user) { setCartCount(0); setWishlistCount(0); return; }
    try {
      const [cartRes, wishRes] = await Promise.all([
        axios.get('/api/cart?flag=C&countOnly=true'),
        axios.get('/api/cart?flag=W&countOnly=true')
      ]);
      setCartCount(cartRes.data.count || 0);
      setWishlistCount(wishRes.data.count || 0);
    } catch {}
  }, [user]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const addToCart = async (productId, quantity = 1) => {
    if (!user) { toast.info('Please sign in to add to cart'); return false; }
    try {
      await axios.post('/api/cart', { productId, quantity, flag: 'C' });
      setCartCount(c => c + 1);
      toast.success('Added to cart!');
      return true;
    } catch (e) { toast.error(e.response?.data?.message || 'Error'); return false; }
  };

  const addToWishlist = async (productId) => {
    if (!user) { toast.info('Please sign in to add to wishlist'); return false; }
    try {
      await axios.post('/api/cart', { productId, quantity: 1, flag: 'W' });
      setWishlistCount(c => c + 1);
      toast.success('Added to wishlist!');
      return true;
    } catch (e) { toast.error(e.response?.data?.message || 'Error'); return false; }
  };

  const removeFromCart = async (productId, flag) => {
    try {
      await axios.delete(`/api/cart/${productId}?flag=${flag}`);
      if (flag === 'C') setCartCount(c => Math.max(0, c - 1));
      else setWishlistCount(c => Math.max(0, c - 1));
      return true;
    } catch { return false; }
  };

  return (
    <AppContext.Provider value={{
      cartCount, wishlistCount, cartItems, wishlistItems,
      addToCart, addToWishlist, removeFromCart, fetchCounts
    }}>
      {children}
    </AppContext.Provider>
  );
};
